import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AccountSummary, Surface, WhatsAppChannel } from '@crosscastr/shared';
import { api } from '../lib/api.js';
import { fmt, maskPhone, SURFACE_LABELS } from '../lib/format.js';
import { stateMeta, warmUpValue } from '../lib/healthUi.js';
import { Icon, type IconName } from '../components/Icon.js';
import { HealthGauge } from '../components/HealthGauge.js';
import { WarmUpMeter } from '../components/WarmUpMeter.js';
import { Tooltip, WARMUP_TIP } from '../components/Tooltip.js';
import { PairingModal } from '../components/PairingModal.js';
import { GroupSetupPanel } from '../components/GroupSetupPanel.js';
import { EmptyState } from '../components/EmptyState.js';
import { SessionsSkeleton, TableSkeleton } from '../components/Skeleton.js';

// "המספרים והקבוצות" — one setup hub. Tab "מספרים": connect a number, see its health/warm-up,
// pick its publish groups (GroupSetupPanel). Tab "קבוצות": the full cross-number group/channel list.
// Merges the former /sessions + /targets (which redirects here).

type Tab = 'numbers' | 'groups';

// "בטיחות מספר" — what each state means and what the operator should do. Score/warm-up/cap come from
// the API; this is the human explanation layer.
const SAFETY_GUIDANCE: Record<string, string> = {
  ok: 'המספר במצב טוב. שלח כרגיל — השליחה הבטוחה שומרת עליו אוטומטית.',
  warm: 'בהרצה: הכמות מוגבלת בכוונה. אל תגדיל ידנית — תן למספר לצבור ותק.',
  risk: 'בסיכון! הפחת שליחה מיד, בדוק אם היו תלונות ספאם, ותן למספר לנוח 24–48 שעות לפני שליחה נוספת.',
  muted: 'לא מחובר. חבר מחדש דרך WhatsApp כדי להתחיל.',
};
const SAFETY_STATE_COLOR: Record<string, string> = { ok: 'var(--ok)', warm: 'var(--warm)', risk: 'var(--risk)', muted: 'var(--text-dim)' };

function SessionCard({ a, onConnect, onSetup }: { a: AccountSummary; onConnect: (a: AccountSummary) => void; onSetup: (id: string) => void }): JSX.Element {
  const meta = stateMeta(a);
  const capPct = a.dailyCap ? Math.min(100, (a.sentToday / a.dailyCap) * 100) : 0;
  const pairing = a.status === 'qr' || a.status === 'connecting';
  const connected = a.status === 'connected';
  const headroom = Math.max(0, a.dailyCap - a.sentToday);
  const stateColor = SAFETY_STATE_COLOR[a.healthState] ?? 'var(--text-dim)';
  return (
    <div className="card session-card">
      <div className="sc-top">
        <div>
          <div className="sc-title">{a.label}</div>
          <div className="phone ltr">{maskPhone(a.phoneNumber)}</div>
        </div>
        <span className={`state ${meta.cls}`}><span className="sd" /> {meta.label}</span>
      </div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 8 }}>
        <HealthGauge score={a.healthScore} state={a.healthState} size={104} />
        <div style={{ flex: 1 }}>
          <div className="gm-row"><span className="k">שלב הרצה <Tooltip text={WARMUP_TIP} /></span><span className="v">{warmUpValue(a.warmUpStage)}</span></div>
          <WarmUpMeter stage={a.warmUpStage} />
          <div className="gm-row" style={{ marginTop: 12 }}><span className="k">כמות יומית</span><span className="v">{fmt(a.sentToday)} / {fmt(a.dailyCap)}</span></div>
          <div className="cap"><span style={{ width: `${capPct}%`, background: meta.capColor }} /></div>
          <div className="gm-row" style={{ marginTop: 12 }}><span className="k">אזור שליחה</span><span className="v">{a.proxyRegion ? `${a.proxyRegion} · קבוע` : '—'}</span></div>
        </div>
      </div>
      <div style={{ marginTop: 14, padding: 12, borderRadius: 'var(--r-sm)', background: 'var(--surface-1)', borderInlineStart: `3px solid ${stateColor}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13 }}>
            <Icon name="bolt" /> מצב המספר
          </span>
          <span style={{ fontSize: 12, color: stateColor, fontWeight: 600 }}>{meta.label} · {a.healthScore}/100</span>
        </div>
        <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--text-dim)', marginBottom: 6 }}>
          <span>שלב: <b style={{ color: 'var(--text)' }}>{a.warmUpLabel}</b></span>
          <span>נותרו היום: <b className="mono" style={{ color: 'var(--text)' }}>{fmt(headroom)}</b></span>
        </div>
        <div style={{ fontSize: 12, lineHeight: 1.6, color: a.healthState === 'risk' ? 'var(--risk)' : 'var(--text-dim)' }}>
          {SAFETY_GUIDANCE[a.healthState] ?? ''}
        </div>
      </div>

      <div className="session-actions">
        {pairing ? (
          <button className="mini" onClick={() => onConnect(a)}><Icon name="phone" /> הצג קוד חיבור</button>
        ) : connected ? (
          <button className="mini" onClick={() => onSetup(a.id)}><Icon name="bolt" /> קבוצות פרסום</button>
        ) : (
          <button className="mini" onClick={() => onConnect(a)}><Icon name="phone" /> חבר מחדש</button>
        )}
      </div>
    </div>
  );
}

type ConnectModal = { mode: 'new' } | { mode: 'existing'; id: string; label: string };

function NumbersTab(): JSX.Element {
  const qc = useQueryClient();
  const { data: accounts, isLoading } = useQuery({ queryKey: ['accounts'], queryFn: api.accounts });
  const [modal, setModal] = useState<ConnectModal | null>(null);
  const [setupId, setSetupId] = useState<string | null>(null);

  const closeModal = (): void => {
    setModal(null);
    qc.invalidateQueries({ queryKey: ['accounts'] });
  };

  const atRisk = (accounts ?? []).filter((a) => a.healthState === 'risk');
  const setupAccount = (accounts ?? []).find((a) => a.id === setupId) ?? null;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn" onClick={() => setModal({ mode: 'new' })}>
          <Icon name="plus" /> חבר מספר חדש
        </button>
      </div>

      {atRisk.length > 0 && (
        <div
          role="alert"
          style={{
            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, padding: '12px 16px',
            borderRadius: 'var(--r-sm)', border: '1px solid var(--risk)',
            background: 'color-mix(in srgb, var(--risk) 12%, transparent)', color: 'var(--risk)', fontSize: 14, fontWeight: 600,
          }}
        >
          <Icon name="bolt" />
          🚨 {atRisk.length === 1 ? `המספר ${atRisk[0].label} ירד לרמת סיכון` : `${atRisk.length} מספרים ירדו לרמת סיכון`} — השליחה מהם מואטת אוטומטית. כדאי לבדוק ולהפחית שליחה.
        </div>
      )}

      {isLoading || !accounts ? (
        <SessionsSkeleton />
      ) : (
        <div className="sessions-grid">
          {accounts.map((a) => (
            <SessionCard
              key={a.id}
              a={a}
              onConnect={(acc) => setModal({ mode: 'existing', id: acc.id, label: acc.label })}
              onSetup={(id) => setSetupId((cur) => (cur === id ? null : id))}
            />
          ))}
        </div>
      )}

      {setupAccount && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 13, color: 'var(--text-dim)', marginBottom: 6 }}>הגדרת קבוצות · {setupAccount.label}</div>
          <GroupSetupPanel accountId={setupAccount.id} onClose={() => setSetupId(null)} />
        </div>
      )}

      {modal?.mode === 'new' && <PairingModal mode="new" onClose={closeModal} onConnected={(id) => setSetupId(id)} />}
      {modal?.mode === 'existing' && (
        <PairingModal mode="existing" accountId={modal.id} label={modal.label} onClose={closeModal} onConnected={(id) => setSetupId(id)} />
      )}
    </>
  );
}

const FILTERS: { key: Surface | 'all'; label: string }[] = [
  { key: 'all', label: 'הכל' },
  { key: 'wa_group', label: 'קבוצות' },
  { key: 'wa_channel', label: 'ערוץ' },
  { key: 'telegram', label: 'Telegram' },
  { key: 'x', label: 'X' },
];
const SURFACE_ICON: Record<string, IconName> = { wa_group: 'wa', wa_channel: 'channel', wa_status: 'status', telegram: 'telegram', x: 'x' };

function GroupsTab(): JSX.Element {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [surface, setSurface] = useState<Surface | 'all'>('all');
  const [pulled, setPulled] = useState<WhatsAppChannel[] | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const { data: targets, isLoading } = useQuery({
    queryKey: ['targets', search, surface],
    queryFn: () => api.targets({ search: search || undefined, surface: surface === 'all' ? undefined : surface }),
  });

  const pull = useMutation({
    mutationFn: () => api.channels(),
    onSuccess: (r) => {
      setPulled(r.channels);
      setNote(r.requiresSession ? 'כדי לטעון ערוצים צריך מספר WhatsApp מחובר.' : r.channels.length ? null : 'לא נמצאו ערוצים.');
    },
  });
  const addChannel = useMutation({
    mutationFn: (c: WhatsAppChannel) => api.selectChannel(c),
    onSuccess: (_r, c) => {
      setPulled((prev) => (prev ? prev.filter((x) => x.externalId !== c.externalId) : prev));
      void qc.invalidateQueries({ queryKey: ['targets'] });
    },
  });

  return (
    <>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn ghost" onClick={() => pull.mutate()} disabled={pull.isPending}>
          <Icon name="channel" /> {pull.isPending ? 'טוען…' : 'טען ערוצים'}
        </button>
        <div className="search">
          <Icon name="search" />
          <input placeholder="חיפוש קבוצה…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {(pulled !== null || note) && (
        <div className="card" style={{ padding: 20, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: pulled?.length ? 12 : 0 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, fontFamily: 'var(--font-display)' }}>ערוצי WhatsApp לטעינה</h3>
            <button className="filter-tabs" style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-dim)' }} onClick={() => { setPulled(null); setNote(null); }}>
              <Icon name="close" />
            </button>
          </div>
          {note && <div style={{ color: 'var(--text-dim)', fontSize: 14 }}>{note}</div>}
          {pulled?.map((c) => (
            <div key={c.externalId} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderTop: '1px solid var(--hair)' }}>
              <span className="chip teal"><Icon name="channel" /> ערוץ</span>
              <div style={{ fontWeight: 600 }}>{c.name}<div className="ltr" style={{ fontSize: 12, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>{c.externalId}</div></div>
              <div className="mono" style={{ marginInlineStart: 'auto', color: 'var(--text-dim)' }}>{fmt(c.memberCount)}</div>
              <button className="btn" onClick={() => addChannel.mutate(c)} disabled={addChannel.isPending}><Icon name="plus" /> הוסף</button>
            </div>
          ))}
        </div>
      )}

      <div className="filter-tabs" style={{ marginBottom: 16 }}>
        {FILTERS.map((f) => (
          <button key={f.key} className={surface === f.key ? 'on' : ''} onClick={() => setSurface(f.key)}>{f.label}</button>
        ))}
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        {isLoading || !targets ? (
          <TableSkeleton rows={5} />
        ) : targets.length === 0 ? (
          <EmptyState
            icon="targets"
            title={search || surface !== 'all' ? 'אין קבוצה מתאימה' : 'עדיין אין קבוצות'}
            sub={search || surface !== 'all'
              ? 'נסה לנקות את החיפוש או את הסינון כדי לראות את כל הקבוצות והערוצים.'
              : 'הקבוצות והערוצים שתחבר יופיעו כאן — עם מספר החברים והקישור שמתווסף לכל הודעה.'}
          />
        ) : (
          <table className="dtable">
            <thead>
              <tr><th>קבוצה</th><th>סוג</th><th>חברים</th><th>קישור בסוף</th><th>מצב</th></tr>
            </thead>
            <tbody>
              {targets.map((t) => (
                <tr key={t.id}>
                  <td style={{ fontWeight: 600 }}>{t.name}<div className="ltr" style={{ fontSize: 12, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>{t.externalId}</div></td>
                  <td><span className="chip teal"><Icon name={SURFACE_ICON[t.surface]} /> {SURFACE_LABELS[t.surface]}</span></td>
                  <td className="mono">{fmt(t.memberCount)}</td>
                  <td style={{ color: 'var(--text-dim)', fontSize: 13 }}>{t.suffix ?? '—'}</td>
                  <td>{t.isActive ? <span className="chip green">פעיל</span> : <span className="chip muted">כבוי</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

const TABS: { key: Tab; label: string }[] = [
  { key: 'numbers', label: 'מספרים' },
  { key: 'groups', label: 'קבוצות' },
];
const SUB: Record<Tab, string> = {
  numbers: 'מצב, שלב הרצה וכמות יומית לכל מספר · חיבור בקלות דרך WhatsApp',
  groups: 'כל הקבוצות והערוצים · מספר חברים וקישור שמתווסף לכל הודעה',
};

export function SessionsPage(): JSX.Element {
  const [tab, setTab] = useState<Tab>('numbers');

  return (
    <div className="content">
      <div className="page-head">
        <div>
          <div className="eyebrow">חיבור</div>
          <h1>המספרים והקבוצות</h1>
          <div className="sub">{SUB[tab]}</div>
        </div>
      </div>

      <div className="filter-tabs" style={{ marginBottom: 20 }}>
        {TABS.map((t) => (
          <button key={t.key} className={tab === t.key ? 'on' : ''} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>

      {tab === 'numbers' ? <NumbersTab /> : <GroupsTab />}
    </div>
  );
}
